<template>
  <div v-if="orderedMembers.length > 1" class="margin-bottom-m">
    <div v-if="productType && isShopTheSetEnabledV2" class="set-carousel-title">
      <div
        class="carousel-title"
        :class="{
          'margin-bottom-xxs aem-bt-secondary-h5': isBcom,
          'margin-bottom-xxxs h6': isMcom,
        }"
      >
        {{ carouselTitle }}
      </div>
      <div class="product-type">
        <span>Selected item: </span>
        <span :class="{ 'c-capitalize': !selectedProductDetail?.productTypeDescription }">
          {{ productType }}
        </span>
      </div>
    </div>
    <div v-else-if="productType" class="set-carousel-title">
      <span>{{ carouselTitle }}</span>
      <span :class="{ 'c-capitalize': !selectedProductDetail?.productTypeDescription }">
        {{ productType }}
      </span>
    </div>
    <Carousel
      :ssr-size="ssrSize"
      :list="orderedMembers"
      :number-of-items="{ large: 6, medium: 4, small: 4.5 }"
      :gutter="{ large: 8, medium: 8, small: 8 }"
    >
      <template #item="{ data }">
        <button
          type="button"
          :aria-pressed="String(selectedConsolidatedMemberId) === String(data.id)"
          :aria-label="`${data.detail?.memberShortName || data.detail?.name}, part of the set${!data.availability.available ? '; unavailable' : ''}`"
          class="member-button"
          @click="handleMemberClick(data.id)"
        >
          <div
            class="image-wrapper"
            :class="{
              'image-wrapper--selected': String(selectedConsolidatedMemberId) === String(data.id),
              'image-wrapper--unavailable': !data.availability.available,
            }"
          >
            <img :src="`${productUrlTemplate}${getMemberImage(data)}`" :alt="data.detail?.memberShortName" />
          </div>
        </button>
      </template>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Carousel from '@atomic-ui/carousel/Carousel';

import { useProduct } from '~/modules/product/composables/useProduct';
import useUrlTemplate from '~/composables/useUrlTemplate';
import { useConsolidatedMember } from '~/composables/useConsolidatedMember';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useExperiments from '~/stores/useExperimentsStore';
import { tagShopTheSetMemberClick } from '~/features/analytics/shopTheSet';
import once from '~/utils/once';
import isUpcsAvaiable from '~/utils/isMemberProductAvailable';
import { getMemberProduct, getMemberProductIds } from '~/utils/product';
import { useBrand } from '~/composables/useBrand';

const { productData } = useProduct();
const { productUrlTemplate } = useUrlTemplate();
const { selectMember, memberImages, selectedMember } = useConsolidatedMember();
const { isTablet, isDesktop, isDesktopMedium } = useDeviceType();
const { isTitleTypeSetFlag } = useProductFlags();
const { isMcom, isBcom } = useBrand();

const {
  experimentFlags: { isShopTheSetEnabledV2 },
} = useExperiments();

const fireShopTheSetTrackingOnce = once(tagShopTheSetMemberClick);

const ssrSize = isDesktop.value || isDesktopMedium.value || isTablet.value ? 'large' : 'small';
const selectedConsolidatedMemberId = computed({
  get() {
    return selectedMember.value;
  },
  set(newProductId) {
    const numberNewProductId = newProductId;
    selectMember(numberNewProductId);
  },
});

const handleMemberClick = (dataId: string) => {
  selectedConsolidatedMemberId.value = Number(dataId);
  fireShopTheSetTrackingOnce();
};

const includeSelectedMemberSort = (memberIds: ReturnType<typeof getMemberProductIds>) => {
  const selectedId = String(selectedConsolidatedMemberId.value);
  return memberIds.sort((a, b) => {
    if (a === selectedId) {
      return -1;
    }
    if (b === selectedId) {
      return 1;
    }
    return 0;
  });
};

const orderedMembers = includeSelectedMemberSort(getMemberProductIds(productData as Product))
  .map((memberId) => getMemberProduct(productData as Product, memberId) || null)
  .filter(isUpcsAvaiable);

const getMemberImage = (member: Product) => {
  const memberId = Number(member.id);
  if (memberId && memberImages.value?.[memberId]) {
    return memberImages.value?.[memberId];
  }
  return member?.imagery?.images[0]?.filePath;
};

const selectedProductDetail = computed(() => getMemberProduct(productData as Product, String(selectedConsolidatedMemberId.value))?.detail);

const productType = computed(() => {
  const typeName = selectedProductDetail.value?.typeName;
  const customerFacingType = selectedProductDetail.value?.productTypeDescription;
  return customerFacingType || typeName?.toLowerCase();
});

const carouselTitle = computed(() => {
  if (isShopTheSetEnabledV2) {
    return `Shop the ${isTitleTypeSetFlag ? 'set' : 'collection'}`;
  }
  return `Part of a ${isTitleTypeSetFlag ? 'set' : 'collection'}: `;
});
</script>






<style lang="scss" scoped>
.member-button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
:deep(.v-scroller) {
  .v-carousel-container {
    // These margins (gutter/2) are required to prevent CLS
    // the atomic-ui/carousel injects these styles dynamically only after client-side mounting
    margin-left: -4px;
    margin-right: -4px;
  }
}

.image-wrapper {
  position: relative;
  box-sizing: border-box;

  img {
    aspect-ratio: 4/5;
    object-fit: cover;
    height: 100%;
  }

  &.image-wrapper--selected {
    border: 2px solid $black;
  }

  &.image-wrapper--unavailable {
    overflow: hidden;

    img {
      opacity: 0.5;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 200%;
      height: 0;
      border-top: 1px solid $gray-1-color;
      border-bottom: 1px solid $white;
      transform-origin: top left;
      transform: rotate(50deg);
      z-index: 1;
    }
  }
}

.set-carousel-title {
  margin-bottom: rem-calc(12);
}
.c-capitalize {
  text-transform: capitalize;
}


.image-wrapper {
  border: 1px solid $gray-1-color;
  border-radius: 5px;
  overflow: hidden;
}
.margin-bottom-xxxs {
  margin-bottom: rem-calc(4);
}
</style>